import { useState, useEffect, useRef, useCallback } from 'react';
import { sanitizeMosTitle } from '@/utils/militaryTranslation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Layout from '@/components/Layout';
import { PageContainer } from '@/components/ui/PageContainer';
import { PageHeader } from '@/components/ui/PageHeader';
import {
  Send, AlertCircle, MessageSquare, FileText,
  Shield, ShieldCheck, ShieldX, Clock, Eye, Building2, User, Briefcase,
  MapPin, Mail, Phone, X, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { AwardsDisplay } from '@/components/AwardsDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 5000;

/* ─── Resume Access Status Badge ─── */
function AccessBadge({ status }) {
  const map = {
    none:     { label: 'No Request',       icon: Shield,      cls: 'text-slate-400 bg-slate-50' },
    pending:  { label: 'Pending',          icon: Clock,       cls: 'text-amber-600 bg-amber-50' },
    approved: { label: 'Access Granted',   icon: ShieldCheck, cls: 'text-emerald-600 bg-emerald-50' },
    denied:   { label: 'Declined',         icon: ShieldX,     cls: 'text-red-500 bg-red-50' },
  };
  const { label, icon: Icon, cls } = map[status] || map.none;
  return (
    <span data-testid="resume-access-badge" className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

/* ─── System Message Card ─── */
function SystemMessage({ msg, user, resumeAccess, onApprove, onDecline, onOpenResume, responding }) {
  const isRequest  = msg.message_type === 'system_resume_request';
  const isGranted  = msg.message_type === 'system_resume_granted';
  const isDeclined = msg.message_type === 'system_resume_declined';
  const isCandidate = user?.role === 'job_seeker';
  const isEmployer  = user?.role === 'employer';
  const isPending = resumeAccess?.status === 'pending';

  return (
    <div data-testid={`system-msg-${msg.message_id}`} className="flex justify-center my-4">
      <div className={`max-w-sm w-full rounded-xl border px-5 py-4 text-center
        ${isGranted ? 'border-emerald-200 bg-emerald-50/60' : isDeclined ? 'border-red-200 bg-red-50/60' : 'border-slate-200 bg-slate-50/60'}
      `}>
        <div className="flex items-center justify-center gap-2 mb-2">
          {isRequest  && <FileText className="w-4 h-4 text-brand" />}
          {isGranted  && <ShieldCheck className="w-4 h-4 text-emerald-600" />}
          {isDeclined && <ShieldX className="w-4 h-4 text-red-500" />}
          <span className="text-sm font-semibold text-slate-700">{
            isRequest ? 'Resume Access Request' : isGranted ? 'Resume Access Granted' : 'Resume Access Declined'
          }</span>
        </div>
        <p className="text-xs text-slate-500 mb-3">{msg.content}</p>

        {/* Candidate action buttons on pending request */}
        {isRequest && isCandidate && isPending && (
          <div className="flex gap-2 justify-center" data-testid="resume-access-actions">
            <Button
              data-testid="allow-access-btn"
              size="sm"
              className="bg-brand hover:bg-brand-hover text-white text-xs h-8 px-4"
              onClick={onApprove}
              disabled={responding}
            >Allow Access</Button>
            <Button
              data-testid="decline-access-btn"
              size="sm"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 text-xs h-8 px-4"
              onClick={onDecline}
              disabled={responding}
            >Decline</Button>
          </div>
        )}

        {/* Employer: open resume after grant */}
        {isGranted && isEmployer && resumeAccess?.status === 'approved' && (
          <Button
            data-testid="open-resume-btn"
            size="sm"
            className="bg-brand hover:bg-brand-hover text-white text-xs h-8 px-4 mt-1"
            onClick={onOpenResume}
          ><Eye className="w-3 h-3 mr-1.5" /> Open Resume</Button>
        )}
      </div>
    </div>
  );
}

/* ─── Conversation Header Card ─── */
function ConversationHeader({ conv, user, resumeAccess, onRequestAccess, onOpenResume, requesting }) {
  const navigate = useNavigate();
  const isEmployer = user?.role === 'employer';
  const application = conv.application || null;
  // When the candidate already applied to this employer's native job, the employer already has
  // the submitted package — surface Open Application, never Request Resume Access.
  const canRequest = isEmployer && !application && (!resumeAccess || resumeAccess.status === 'none' || resumeAccess.status === 'denied');
  const canOpen    = isEmployer && !application && resumeAccess?.status === 'approved';

  return (
    <div data-testid="conversation-header" className="px-5 py-4 border-b border-slate-100 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isEmployer ? (
              <>
                <User className="w-4 h-4 text-brand flex-shrink-0" />
                <h3 data-testid="header-candidate-name" className="font-semibold text-slate-900 truncate">
                  {conv.candidate_name || 'Candidate'}
                </h3>
              </>
            ) : (
              <>
                <Building2 className="w-4 h-4 text-brand flex-shrink-0" />
                <h3 data-testid="header-company-name" className="font-semibold text-slate-900 truncate">
                  {conv.company_name || 'Unknown Company'}
                </h3>
              </>
            )}
          </div>
          {conv.job_title && (
            <p data-testid="header-job-title" className="text-sm text-slate-600 ml-6">
              {conv.job_title}{conv.job_location ? ` — ${conv.job_location}` : ''}
            </p>
          )}
          <div className="flex items-center gap-4 mt-1.5 ml-6">
            {isEmployer ? (
              conv.company_name && (
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <Building2 className="w-3 h-3" /> {conv.company_name}
                </span>
              )
            ) : (
              conv.recruiter_name && (
                <span data-testid="header-recruiter" className="flex items-center gap-1 text-xs text-slate-500">
                  <Briefcase className="w-3 h-3" /> Recruiter: {conv.recruiter_name}
                </span>
              )
            )}
            <AccessBadge status={resumeAccess?.status || 'none'} />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isEmployer && application && (
            <Button
              data-testid="open-application-btn"
              size="sm"
              className="bg-brand hover:bg-brand-hover text-white text-xs h-8"
              onClick={() => navigate(`/applications/${application.application_id}`)}
            ><FileText className="w-3 h-3 mr-1.5" /> Open Application</Button>
          )}
          {canRequest && (
            <Button
              data-testid="request-resume-access-btn"
              size="sm"
              className="bg-brand hover:bg-brand-hover text-white text-xs h-8"
              onClick={onRequestAccess}
              disabled={requesting}
            ><FileText className="w-3 h-3 mr-1.5" /> Request Resume Access</Button>
          )}
          {canOpen && (
            <Button
              data-testid="header-open-resume-btn"
              size="sm"
              variant="outline"
              className="border-brand text-brand hover:bg-brand-light text-xs h-8"
              onClick={onOpenResume}
            ><Eye className="w-3 h-3 mr-1.5" /> Open Resume</Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Sidebar Conversation Entry ─── */
function ConversationEntry({ conv, isSelected, isUnread, onClick, user }) {
  const isEmployer = user?.role === 'employer';
  const displayName = isEmployer
    ? (conv.candidate_name || 'Candidate')
    : (conv.company_name || 'Unknown Company');
  const DisplayIcon = isEmployer ? User : Building2;

  return (
    <button
      data-testid={`conversation-${conv.conversation_id}`}
      onClick={onClick}
      className={`w-full text-left px-4 py-3.5 rounded-xl transition-all duration-150 relative group ${
        isSelected
          ? 'bg-brand text-white shadow-sm'
          : isUnread
            ? 'bg-brand-light/50 hover:bg-brand-light border-l-[3px] border-brand'
            : 'hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        <DisplayIcon className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-white/80' : 'text-brand'}`} />
        <span className={`text-sm font-semibold truncate ${isSelected ? 'text-white' : 'text-slate-900'}`}>
          {displayName}
        </span>
        {isUnread && !isSelected && (
          <span className="ml-auto w-2 h-2 rounded-full bg-brand flex-shrink-0" data-testid={`unread-indicator-${conv.conversation_id}`} />
        )}
      </div>
      {conv.job_title && (
        <p className={`text-xs truncate mb-1 ml-5 ${isSelected ? 'text-white/70' : 'text-slate-500'}`}>
          {conv.job_title}
        </p>
      )}
      {conv.last_message && (
        <p className={`text-xs truncate ml-5 ${isSelected ? 'text-white/60' : 'text-slate-400'}`}>
          {conv.last_message}
        </p>
      )}
      {conv.resume_access_status && conv.resume_access_status !== 'none' && !isSelected && (
        <div className="ml-5 mt-1.5">
          <AccessBadge status={conv.resume_access_status} />
        </div>
      )}
    </button>
  );
}

/* ─── Main Page ─── */
export default function MessagingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoSelectedRef = useRef(null);
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [unreadConversationIds, setUnreadConversationIds] = useState(new Set());
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [resumeAccess, setResumeAccess] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [responding, setResponding] = useState(false);
  const [resumeViewerOpen, setResumeViewerOpen] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [loadingResume, setLoadingResume] = useState(false);
  const [resumeDocTab, setResumeDocTab] = useState('resume');
  const [resumeStatus, setResumeStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  /* ── Data loading ── */
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(r => {
        setUser(r.data);
        if (r.data?.role === 'job_seeker') {
          axios.get(`${BACKEND_URL}/api/resume`, { withCredentials: true })
            .then(res => setResumeStatus({ is_complete: res.data.is_complete, visible: res.data.visible }))
            .catch(() => setResumeStatus({ is_complete: false, visible: false }));
        }
      })
      .catch(() => {});
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/messages/conversations`, { withCredentials: true });
      setConversations(r.data);
    } catch {}
  }, []);

  const loadUnreadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/messages/unread-count`, { withCredentials: true });
      setUnreadConversationIds(new Set(r.data.conversations?.map(c => c.conversation_id) || []));
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) return;
    loadConversations();
    loadUnreadStatus();
    pollingRef.current = setInterval(() => { loadConversations(); loadUnreadStatus(); }, POLL_INTERVAL);
    return () => clearInterval(pollingRef.current);
  }, [user, loadConversations, loadUnreadStatus]);

  /* ── Message + resume access loading ── */
  const loadMessages = async (convId) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/messages/conversation/${convId}`, { withCredentials: true });
      setMessages(r.data);
    } catch {}
  };

  const loadResumeAccess = async (convId) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/messages/resume-access/conversation/${convId}`, { withCredentials: true });
      setResumeAccess(r.data);
    } catch {
      setResumeAccess({ status: 'none' });
    }
  };

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.conversation_id);
      loadResumeAccess(selectedConversation.conversation_id);
      // Mark read
      axios.put(`${BACKEND_URL}/api/messages/conversation/${selectedConversation.conversation_id}/mark-read`, {}, { withCredentials: true })
        .then(() => {
          setUnreadConversationIds(prev => { const n = new Set(prev); n.delete(selectedConversation.conversation_id); return n; });
        }).catch(() => {});
    }
  }, [selectedConversation]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  /* ── Deep-link auto-select: preserve the exact originating conversation ──
     Supports (in priority): ?conversation=<id> · ?application=<application_id> ·
     ?applicant=/?candidate=<user_id> (only when unambiguous). Selects once per unique param
     set; falls back gracefully to the inbox if the target can't be resolved. Never guesses a
     candidate's conversation when multiple exist. */
  useEffect(() => {
    const convId = searchParams.get('conversation');
    const appId = searchParams.get('application');
    const userId = searchParams.get('applicant') || searchParams.get('candidate');
    const key = convId || appId || userId;
    if (!key || conversations.length === 0) return;
    if (autoSelectedRef.current === key) return;

    let target = null;
    if (convId) {
      target = conversations.find(c => c.conversation_id === convId) || null;
    } else if (appId) {
      target = conversations.find(c => c.application?.application_id === appId) || null;
    } else if (userId) {
      const matches = conversations.filter(c => c.job_seeker_id === userId || c.candidate_id === userId);
      // Only auto-select when unambiguous; never guess among multiple applications.
      if (matches.length === 1) target = matches[0];
    }

    autoSelectedRef.current = key;
    if (target) {
      setSelectedConversation(target);
    }
    // Clear the param so refreshes/back behave like the normal inbox afterward.
    const next = new URLSearchParams(searchParams);
    ['conversation', 'application', 'applicant', 'candidate'].forEach(k => next.delete(k));
    setSearchParams(next, { replace: true });
  }, [conversations, searchParams, setSearchParams]);

  /* ── Actions ── */
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/messages/send`, {
        conversation_id: selectedConversation.conversation_id, content: newMessage
      }, { withCredentials: true });
      setNewMessage('');
      loadMessages(selectedConversation.conversation_id);
      loadConversations();
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  const handleReportAbuse = async () => {
    const reason = window.prompt('Please describe the issue:');
    if (!reason) return;
    try {
      await axios.post(`${BACKEND_URL}/api/messages/report-abuse`, {
        conversation_id: selectedConversation.conversation_id, reason
      }, { withCredentials: true });
      toast.success('Report submitted. An admin will review it.');
    } catch { toast.error('Failed to submit report'); }
  };

  const handleRequestAccess = async () => {
    setRequesting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/messages/resume-access/request`, {
        conversation_id: selectedConversation.conversation_id,
      }, { withCredentials: true });
      toast.success('Resume access requested');
      loadMessages(selectedConversation.conversation_id);
      loadResumeAccess(selectedConversation.conversation_id);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request access');
    } finally { setRequesting(false); }
  };

  const handleRespondAccess = async (action) => {
    if (!resumeAccess?.id) return;
    setResponding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/messages/resume-access/${resumeAccess.id}/respond`, { action }, { withCredentials: true });
      toast.success(action === 'approve' ? 'Resume access granted' : 'Request declined');
      loadMessages(selectedConversation.conversation_id);
      loadResumeAccess(selectedConversation.conversation_id);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to respond');
    } finally { setResponding(false); }
  };

  const handleOpenResume = async () => {
    if (!resumeAccess?.id) return;
    setLoadingResume(true);
    setResumeDocTab('resume');
    setResumeViewerOpen(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/messages/resume-access/${resumeAccess.id}/resume`, { withCredentials: true });
      setResumeData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load resume');
      setResumeViewerOpen(false);
    } finally {
      setLoadingResume(false);
    }
  };

  /* ── Render ── */
  const conv = selectedConversation;

  return (
    <Layout user={user}>
      <PageContainer>
        <PageHeader
          icon={MessageSquare}
          title="Conversations"
          subtitle="Your conversations"
        />

        {/* Job Seeker Resume Banners */}
        {user?.role === 'job_seeker' && resumeStatus && !resumeStatus.is_complete && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="resume-incomplete-banner">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Complete your resume to connect with employers</p>
              <p className="text-xs text-amber-600 mt-0.5">Employers need to see your experience and qualifications.</p>
            </div>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate('/resume-builder')} data-testid="complete-resume-btn">
              Complete Resume
            </Button>
          </div>
        )}
        {user?.role === 'job_seeker' && resumeStatus && resumeStatus.is_complete && !resumeStatus.visible && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3" data-testid="resume-visibility-banner">
            <Eye className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">Make your profile visible to employers</p>
              <p className="text-xs text-blue-600 mt-0.5">Your resume is complete but hidden. Employers can't find you in search.</p>
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
              try {
                const res = await axios.put(`${BACKEND_URL}/api/resume`, { visible: true }, { withCredentials: true });
                setResumeStatus({ is_complete: res.data.is_complete, visible: res.data.visible });
                toast.success('Profile is now visible to employers');
              } catch { toast.error('Failed to update visibility'); }
            }} data-testid="make-visible-btn">
              Make Visible
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-4" style={{ height: 'calc(100vh - 220px)' }}>
          {/* ── Sidebar ── */}
          <div data-testid="conversations-list" className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Conversations</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {conversations.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-12">No conversations yet. They&apos;ll appear here as you reach out.</p>
              ) : conversations.map(c => (
                <ConversationEntry
                  key={c.conversation_id}
                  conv={c}
                  isSelected={conv?.conversation_id === c.conversation_id}
                  isUnread={unreadConversationIds.has(c.conversation_id)}
                  onClick={() => setSelectedConversation(c)}
                  user={user}
                />
              ))}
            </div>
          </div>

          {/* ── Chat Area ── */}
          <div data-testid="messages-area" className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col">
            {conv ? (
              <>
                {/* Header card */}
                <ConversationHeader
                  conv={conv}
                  user={user}
                  resumeAccess={resumeAccess}
                  onRequestAccess={handleRequestAccess}
                  onOpenResume={handleOpenResume}
                  requesting={requesting}
                />

                {/* Sub-bar: report */}
                <div className="flex items-center justify-between px-5 py-2 border-b border-slate-50 bg-slate-50/50">
                  <p className="text-xs text-slate-400">
                    Started {new Date(conv.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                  <button
                    data-testid="report-abuse-btn"
                    onClick={handleReportAbuse}
                    className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition-colors"
                  >
                    <AlertCircle className="w-3 h-3" /> Report
                  </button>
                </div>

                {/* Messages thread */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="messages-list">
                  {messages.map(msg => {
                    const isSystem = msg.sender_type === 'system';
                    if (isSystem) {
                      return (
                        <SystemMessage
                          key={msg.message_id}
                          msg={msg}
                          user={user}
                          resumeAccess={resumeAccess}
                          onApprove={() => handleRespondAccess('approve')}
                          onDecline={() => handleRespondAccess('deny')}
                          onOpenResume={handleOpenResume}
                          responding={responding}
                        />
                      );
                    }
                    const isMine = msg.sender_id === user?.user_id;
                    const isDeleted = msg.sender_deleted === true || msg.sender_id === null;
                    return (
                      <div key={msg.message_id} data-testid={`message-${msg.message_id}`}
                        className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] px-4 py-2.5 text-sm leading-relaxed
                          ${isDeleted
                            ? 'bg-slate-50 text-slate-400 rounded-2xl rounded-bl-md border border-dashed border-slate-200 italic'
                            : isMine
                              ? 'bg-brand text-white rounded-2xl rounded-br-md'
                              : 'bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md'}
                        `}>
                          {isDeleted && <p className="text-[10px] font-medium text-slate-400 mb-0.5">Deleted User</p>}
                          <p>{msg.content}</p>
                          <p className={`text-[10px] mt-1 ${isMine ? 'text-white/50' : 'text-slate-400'}`}>
                            {new Date(msg.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="px-5 py-3 border-t border-slate-100 bg-white">
                  <div className="flex gap-2">
                    <Input
                      data-testid="message-input"
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="h-11 flex-1 rounded-xl border-slate-200 focus:border-brand focus:ring-brand/20"
                      disabled={sending}
                    />
                    <Button
                      data-testid="send-message-btn"
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="h-11 w-11 rounded-xl bg-brand hover:bg-brand-hover text-white p-0"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
                  <MessageSquare className="w-7 h-7 text-slate-300" />
                </div>
                <p className="text-slate-600 text-base font-medium mb-1">Select a conversation</p>
                <p className="text-slate-400 text-sm">Choose from the list on the left to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </PageContainer>

      {/* Resume Viewer Dialog — employer stays in context */}
      <Dialog open={resumeViewerOpen} onOpenChange={(open) => { if (!open) { setResumeViewerOpen(false); setResumeData(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="resume-viewer-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold text-slate-900">
              {loadingResume ? 'Loading...' : `${resumeData?.personal_info?.full_name || 'Candidate'} — Application Documents`}
            </DialogTitle>
          </DialogHeader>

          {loadingResume ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand" />
            </div>
          ) : resumeData ? (
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              {/* Tabs */}
              <div className="flex-shrink-0 flex border-b border-slate-200 mb-4">
                <button
                  data-testid="resume-tab-btn"
                  onClick={() => setResumeDocTab('resume')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    resumeDocTab === 'resume'
                      ? 'border-brand text-brand'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >Resume</button>
                <button
                  data-testid="cover-letter-tab-btn"
                  onClick={() => setResumeDocTab('cover')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    resumeDocTab === 'cover'
                      ? 'border-brand text-brand'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >Cover Letter</button>
              </div>

              {/* Content */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {resumeDocTab === 'resume' ? (
                  <div className="bg-white border border-slate-200 rounded-lg p-6">
                    <div className="border-b border-slate-200 pb-4 mb-4">
                      <h3 className="text-xl font-bold text-slate-900">{resumeData.personal_info?.full_name || 'Candidate'}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
                        {resumeData.personal_info?.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 flex-shrink-0" /> {resumeData.personal_info.city}{resumeData.personal_info.state ? `, ${resumeData.personal_info.state}` : ''}
                          </span>
                        )}
                        {resumeData.personal_info?.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4 flex-shrink-0" /> {resumeData.personal_info.email}
                          </span>
                        )}
                        {resumeData.personal_info?.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4 flex-shrink-0" /> {resumeData.personal_info.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    {resumeData.professional_summary && (
                      <div className="mb-5">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Professional Summary</h4>
                        <p className="text-sm text-slate-700 leading-relaxed">{resumeData.professional_summary}</p>
                      </div>
                    )}
                    {(() => {
                      if (!resumeData.military_roles?.length) return null;
                      const all = [];
                      for (const role of resumeData.military_roles) {
                        for (const item of (role.selected_abilities || [])) { const n = typeof item === 'object' ? item.element_name : item; if (n && !all.includes(n)) all.push(n); }
                        for (const item of (role.selected_skills || [])) { const n = typeof item === 'object' ? item.element_name : item; if (n && !all.includes(n)) all.push(n); }
                        for (const item of (role.selected_knowledge || [])) { const n = typeof item === 'object' ? item.element_name : item; if (n && !all.includes(n)) all.push(n); }
                      }
                      const seen = new Set();
                      const comps = all.filter(c => { const l = c.toLowerCase().trim(); if (seen.has(l)) return false; seen.add(l); return true; }).slice(0, 15);
                      if (!comps.length) return null;
                      return (
                        <div className="mb-5">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core Competencies</h4>
                          <p className="text-sm text-slate-700 leading-relaxed">{comps.join(' \u2022 ')}</p>
                        </div>
                      );
                    })()}
                    {(() => {
                      const wa = resumeData.military_roles?.find(r => r.mos_data?.work_activities?.length > 0)?.mos_data?.work_activities;
                      if (!wa || wa.length === 0) return null;
                      return (
                        <div className="mb-5">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core Responsibilities</h4>
                          <ul className="space-y-0.5">
                            {wa.slice(0, 6).map((a, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&bull;</span><span>{a.name}</span></li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                    {(() => {
                      const tt = resumeData.military_roles?.find(r => r.mos_data?.tools_technology?.length > 0)?.mos_data?.tools_technology;
                      if (!tt || tt.length === 0) return null;
                      return (
                        <div className="mb-5">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tools & Systems</h4>
                          <p className="text-sm text-slate-700 leading-relaxed">{tt.slice(0, 6).map(t => t.category).join(' \u2022 ')}</p>
                        </div>
                      );
                    })()}
                    {resumeData.military_roles?.length > 0 && (
                      <div className="mb-5">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Military Experience</h4>
                        {resumeData.military_roles.map((role, idx) => {
                          const bullets = role.ai_summary_text ? role.ai_summary_text.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#')).map(l => l.replace(/^[•\-\*▪◦●]\s*/, '')) : [];
                          return (
                            <div key={idx} className="mb-3 pl-3 border-l-2 border-brand">
                              <p className="font-medium text-slate-900">{role.rank}, {role.branch}</p>
                              <p className="text-sm text-slate-600">{role.mos_code} - {sanitizeMosTitle(role.mos_title, role.rank)}</p>
                              <p className="text-xs text-slate-400 font-mono">{role.location} | {role.start_month}/{role.start_year} - {role.end_month}/{role.end_year}</p>
                              {bullets.length > 0 && (
                                <ul className="mt-2 space-y-1">
                                  {bullets.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&bull;</span><span>{b}</span></li>)}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {resumeData.civilian_experience?.length > 0 && (
                      <div className="mb-5">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Professional Experience</h4>
                        {resumeData.civilian_experience.map((exp, idx) => (
                          <div key={idx} className="mb-3">
                            <p className="font-medium text-slate-900">{exp.job_title}</p>
                            <p className="text-sm text-slate-600">{exp.company} - {exp.location}</p>
                            <p className="text-xs text-slate-400 font-mono">{exp.start_month}/{exp.start_year} - {exp.is_current ? 'Present' : `${exp.end_month}/${exp.end_year}`}</p>
                            {exp.description && <p className="mt-1 text-sm text-slate-700">{exp.description}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                    {resumeData.education?.length > 0 && (
                      <div className="mb-5">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Education</h4>
                        {resumeData.education.map((edu, idx) => (
                          <div key={idx} className="mb-2">
                            <p className="font-medium text-slate-900">{edu.degree_or_cert}</p>
                            <p className="text-sm text-slate-500">{edu.institution}{edu.end_year ? ` (${edu.end_year})` : ''}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <AwardsDisplay resume={resumeData} />
                  </div>
                ) : (
                  /* Cover Letter Tab */
                  resumeData.cover_letter_text && resumeData.cover_letter_text.length > 50 ? (
                    <div className="bg-white border border-slate-200 rounded-lg p-6">
                      <div className="border-b border-slate-200 pb-4 mb-4">
                        <h3 className="text-xl font-bold text-slate-900">{resumeData.personal_info?.full_name || 'Candidate'}</h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
                          {(resumeData.personal_info?.city || resumeData.personal_info?.state) && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              {[resumeData.personal_info?.city, resumeData.personal_info?.state].filter(Boolean).join(', ')}
                            </span>
                          )}
                          {resumeData.personal_info?.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4 flex-shrink-0" /> {resumeData.personal_info.email}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-4">
                        {resumeData.cover_letter_text.split('\n\n').map((para, idx) => (
                          <p key={idx} className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{para.trim()}</p>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <FileText className="w-16 h-16 text-slate-300 mb-4" />
                      <p className="text-slate-600 font-medium">No cover letter available</p>
                      <p className="text-sm text-slate-500 mt-1">The candidate has not generated a cover letter yet</p>
                    </div>
                  )
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
